import React, { useEffect, useState } from 'react';
import { Trash2, Music, AlertCircle, Loader } from 'lucide-react';
import '../styles/audiolist.css';

const AudioList = () => {
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchAudioFiles();
  }, []);

  const fetchAudioFiles = async () => {
    try {
      const response = await fetch('https://quiz-server-ghre.onrender.com/api/quiz-audio');
      if (!response.ok) throw new Error('Failed to fetch quiz audio files');
      const data = await response.json();
      setAudioFiles(data);
      setError(null);
    } catch (error) {
      setError('Error loading quiz audio files. Please try again later.');
      console.error('Error fetching quiz audio files:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteAudioFile = async (publicId) => {
    if (!window.confirm('Are you sure you want to delete this audio file?')) {
      return;
    }

    setDeleteInProgress(publicId);
    try {
      // Remove the folder prefix if it exists
      const cleanPublicId = publicId.replace('quiz-audio/', '');
      console.log('Attempting to delete:', cleanPublicId);
      
      const response = await fetch(`https://quiz-server-ghre.onrender.com/api/quiz-audio/${cleanPublicId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.details || 'Failed to delete file';
        } catch (e) {
          errorMessage = `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      setAudioFiles(prevFiles => prevFiles.filter(file => file.id !== publicId));
      setSuccessMessage('Quiz audio deleted successfully');
      setError(null);
      
      // Refresh the list after successful deletion
      await fetchAudioFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      setError(error.message);
      setSuccessMessage(null);
    } finally {
      setDeleteInProgress(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container" role="status">
        <Loader className="loading-spinner" />
        <p>Loading quiz audio files...</p>
      </div>
    );
  }

  return (
    <div className="audio-container" role="main">
      <h1 className="audio-title">Quiz Audio Library</h1>
      
      {error && (
        <div className="error-message" role="alert">
          <AlertCircle className="error-icon" />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message" role="status">
          {successMessage}
        </div>
      )}

      {audioFiles.length > 0 ? (
        <ul className="audio-list" role="list">
          {audioFiles.map(file => (
            <li 
              key={file.id} 
              className="audio-item"
              role="listitem"
            >
              <div className="file-header">
                <div className="file-info">
                  <Music className="file-icon" />
                  <span className="file-name">
                    {`${file.username}'s Quiz ${file.quizId}`}
                  </span>
                  <span className="file-date">
                    {new Date(file.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <button
                  className={`delete-button ${deleteInProgress === file.id ? 'deleting' : ''}`}
                  onClick={() => deleteAudioFile(file.id)}
                  disabled={deleteInProgress === file.id}
                  aria-label={`Delete ${file.username}'s quiz audio`}
                >
                  {deleteInProgress === file.id ? (
                    <Loader className="delete-spinner" />
                  ) : (
                    <Trash2 size={18} />
                  )}
                </button>
              </div>

              <div className="audio-player-container">
                <audio 
                  controls 
                  className="audio-player"
                  preload="metadata"
                >
                  <source src={file.audioUrl} type="audio/webm" />
                  Your browser does not support the audio element.
                </audio>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="empty-state">
          <Music size={48} className="empty-icon" />
          <p>No quiz audio files found.</p>
          <p className="empty-hint">Students' quiz recordings will appear here.</p>
        </div>
      )}
    </div>
  );
};

export default AudioList;